import React, { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import {
  Description,
  FormItem,
  FormRow,
  InputLabel,
  Required,
} from "../../../../../components/ControlledInputs/styles";
import { FormContainer, LayoutContainer, TextsContainer } from "./styles";
import { FlexContainer, Subtitle, Wrapper } from "../../../styles";

import { FiArrowRight } from "react-icons/fi";

import SquareShape from "../../Shapes/Squares";
import Button, { Return } from "../../Button";
import ControlledInput, {
  ErrorMessage,
} from "../../../../../components/ControlledInputs/input";
import { Upload, message } from "antd";

import { pickSquareDefaultPositions } from "../../Shapes/shapes-utils";

const TextSchema = z.object({
  primaryText: z.string().min(1),
  secondaryText: z.string().min(1),
  callText: z.string().min(1),
  logo: z.union([
    z.instanceof(File),
    z
      .object({
        name: z.string(),
        type: z.string(),
        lastModified: z.number(),
        thumbUrl: z.string().optional(),
      })
      .transform((data) => {
        return transformToFile(data);
      }),
  ]),
});

export default function LayoutTextForm({
  nextStep,
  previousStep,
  layout,
  confirmedImage,
  setTexts,
  texts,
  setLogoSrc,
  logoSrc,
  setLogoFileList,
  logoFileList,
}) {
  const intl = useIntl();
  const form = useForm({
    resolver: zodResolver(TextSchema),
    defaultValues: { ...texts, logo: logoFileList[0] } ?? {
      primaryText: "",
      secondaryText: "",
      callText: "",
      logo: null,
    },
  });

  const { control, handleSubmit, formState, setValue, watch, clearErrors } =
    form;

  const handleLogoUpload = (file) => {
    const isValidType = ["image/png"].includes(file.type);
    if (!isValidType) {
      message.error("You can only upload PNG or JPG/JPEG files!");
      return Upload.LIST_IGNORE;
    }

    // Validate file size (1MB = 1024 * 1024 bytes)
    const isLessThan1MB = file.size / 1024 / 1024 < 1;
    if (!isLessThan1MB) {
      message.error("Image must be smaller than 1MB!");
      return Upload.LIST_IGNORE;
    }

    setValue("logo", file);
    clearErrors("logo");
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setLogoSrc(reader.result);
    };
    return false;
  };

  const handleDeleteFile = useCallback(() => {
    setLogoSrc(null);
    setLogoFileList([]);
    setValue("logo", null);
  }, [setLogoFileList, setLogoSrc, setValue]);

  const watchPrimaryText = watch("primaryText");
  const watchSecondaryText = watch("secondaryText");
  const watchCallText = watch("callText");

  const onSubmit = ({ primaryText, secondaryText, callText }) => {
    setTexts({ primaryText, secondaryText, callText });
    nextStep();
  };
  return (
    <Wrapper animated={!Boolean(texts.primaryText)}>
      <Return previousStep={previousStep} />
      <TextsContainer>
        <FormContainer>
          <Subtitle>
            <FormattedMessage id={"ai_cretor.texts.title"} />
          </Subtitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormRow marginBottom={"12px"}>
              <ControlledInput
                creator
                name={"primaryText"}
                control={control}
                formState={formState}
                label={"ai_creator.layout.primaryText"}
                required={true}
                max={25}
              />
            </FormRow>
            <FormRow marginBottom={"12px"}>
              <ControlledInput
                creator
                name={"secondaryText"}
                control={control}
                formState={formState}
                label={"ai_creator.layout.secondaryText"}
                required={true}
                max={25}
              />
            </FormRow>
            <FormRow marginBottom={"12px"}>
              <ControlledInput
                creator
                name={"callText"}
                control={control}
                formState={formState}
                label={"ai_creator.layout.callText"}
                required={true}
                max={20}
              />
            </FormRow>
            <FormRow marginBottom={"12px"}>
              <FormItem>
                <InputLabel creator>
                  Logo<Required>*</Required>{" "}
                  <Description creator>
                    {intl.formatMessage({
                      id: `ai_cretor.text-form.upload.desc`,
                    })}
                  </Description>
                </InputLabel>
                <Upload
                  accept="image/png"
                  listType="picture"
                  showUploadList={true}
                  beforeUpload={handleLogoUpload}
                  fileList={logoFileList}
                  maxCount={1}
                  onChange={({ fileList }) => {
                    return fileList.length > 0
                      ? setLogoFileList(fileList)
                      : handleDeleteFile();
                  }}
                >
                  <Button
                    theme="upload"
                    errorMessage={formState?.errors?.["logo"]?.message}
                    label={watch("logo") ? "changeFile" : "chooseFile"}
                  />
                </Upload>
                <ErrorMessage
                  errorMessage={formState?.errors?.["logo"]?.message}
                />
              </FormItem>
            </FormRow>

            <FlexContainer direction={"row"} gap={"24px"}>
              <Button
                size={"modal"}
                label={"foward"}
                onClick={handleSubmit(onSubmit)}
                icon={<FiArrowRight size={20} />}
              />
            </FlexContainer>
          </form>
        </FormContainer>
        <LayoutContainer>
          <SquareShape
            logoSrc={logoSrc}
            imageUrl={confirmedImage}
            shape={layout.layout}
            size={370}
            variantColor={layout.color}
            primaryText={
              watchPrimaryText?.length > 0 ? watchPrimaryText : undefined
            }
            secondaryText={
              watchSecondaryText?.length > 0 ? watchSecondaryText : undefined
            }
            callText={watchCallText?.length > 0 ? watchCallText : undefined}
            imagePosition={
              pickSquareDefaultPositions({
                shape: 5,
                size: 370,
              }).imagePosition
            }
          />
        </LayoutContainer>
      </TextsContainer>
    </Wrapper>
  );
}

function transformToFile(data) {
  if (data.originFileObj instanceof File) {
    return data.originFileObj;
  }
  if (data.thumbUrl?.includes("base64")) {
    const base64Data = data.thumbUrl.split(",")[1];
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: data.type });

    return new File([blob], data.name, {
      type: data.type,
      lastModified: data.lastModified,
    });
  }
  return new File([], data.name, {
    type: data.type,
    lastModified: data.lastModified,
  });
}
